import React, { useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import {
  cancelRideAsAdmin, rebroadcastRideAsAdmin, formatTime, phoneHref, waHref,
} from '../services/firebase';
import { getStuckRides, STATUS_LABEL, STATUS_CLASS } from '../utils/rides';
import { useLang } from '../lang/LangContext';

const REASON_LABEL = {
  searching: 'Searching too long',
  assigned_stale: 'Assigned — no progress',
  arrived_stale: 'Arrived — no start',
};

export default function StuckRides({ rides, onSelectRide, isSuperAdmin = false }) {
  const { t } = useLang();
  const [busy, setBusy] = useState(null);
  const stuck = useMemo(() => getStuckRides(rides), [rides]);

  const act = async (id, fn, ok) => {
    if (!isSuperAdmin) {
      toast.error('Cancelling or rebroadcasting stuck rides requires Super Admin privileges');
      return;
    }
    setBusy(id);
    try {
      await fn();
      toast.success(ok);
    } catch (e) {
      console.error(e);
      toast.error('Action failed');
    } finally {
      setBusy(null);
    }
  };

  return (
    <>
      <div className="card alert-card" style={{ marginBottom: 12 }}>
        <div className="card-title">{t('Stuck ride queue')}</div>
        <p className="muted" style={{ fontSize: 13 }}>
          Searching &gt;5 min, or assigned/arrived with no update &gt;10 min.
          Rebroadcast clears the captain and notifies online captains again.
        </p>
      </div>

      <div className="toolbar">
        <span className="muted">{stuck.length} stuck ride{stuck.length === 1 ? '' : 's'}</span>
      </div>

      <div className="card">
        {stuck.length === 0 ? (
          <div className="empty">{t('No stuck rides — looking good')}</div>
        ) : (
          <div className="table-wrap">
            <table className="data">
              <thead>
                <tr>
                  <th>ISSUE</th>
                  <th>STATUS</th>
                  <th>ROUTE</th>
                  <th>PASSENGER</th>
                  <th>CAPTAIN</th>
                  <th>AGE</th>
                  <th>ACTIONS</th>
                </tr>
              </thead>
              <tbody>
                {stuck.map((r) => (
                  <tr key={r.id} className="row-urgent">
                    <td>
                      <span className="badge orange">{REASON_LABEL[r.stuckReason] || r.stuckReason}</span>
                    </td>
                    <td>
                      <span className={`badge ${STATUS_CLASS[r.status] || 'gray'}`}>
                        {STATUS_LABEL[r.status] || r.status}
                      </span>
                    </td>
                    <td>
                      <div style={{ fontSize: 13 }}>
                        {(r.pickup?.address || '—').slice(0, 32)}
                      </div>
                      <div className="muted">→ {r.destination?.name || '—'}</div>
                      <div className="mono muted" style={{ fontSize: 11 }}>₹{r.fare}</div>
                    </td>
                    <td>
                      <div>{r.customerName || '—'}</div>
                      <div className="muted">{r.customerPhone}</div>
                    </td>
                    <td>
                      {r.driverName || '—'}
                      {r.driverPhone && <div className="muted">{r.driverPhone}</div>}
                    </td>
                    <td className="mono">{r.stuckMins}m</td>
                    <td>
                      <div className="row-actions">
                        {phoneHref(r.customerPhone) && (
                          <a className="btn btn-ghost btn-sm" href={phoneHref(r.customerPhone)}>{t('Call pax')}</a>
                        )}
                        {waHref(r.customerPhone) && (
                          <a className="btn btn-ghost btn-sm" href={waHref(r.customerPhone)} target="_blank" rel="noreferrer">WA</a>
                        )}
                        {isSuperAdmin && (r.status === 'searching' || r.status === 'driver_assigned' || r.status === 'arrived') && (
                          <button
                            type="button"
                            className="btn btn-orange btn-sm"
                            disabled={busy === r.id}
                            onClick={() => act(
                              r.id,
                              () => rebroadcastRideAsAdmin(r.id),
                              'Rebroadcast to captains',
                            )}
                          >
                            Rebroadcast
                          </button>
                        )}
                        {isSuperAdmin && (
                          <button
                            type="button"
                            className="btn btn-red btn-sm"
                            disabled={busy === r.id}
                            onClick={() => {
                              if (!window.confirm('Cancel this ride?')) return;
                              act(r.id, () => cancelRideAsAdmin(r.id, 'Cancelled by admin (stuck)'), 'Cancelled');
                            }}
                          >
                            Cancel
                          </button>
                        )}
                        {onSelectRide && (
                          <button
                            type="button"
                            className="btn btn-ghost btn-sm"
                            style={{ fontWeight: 700 }}
                            onClick={() => onSelectRide(r)}
                            title="Inspect full ride in Rides console"
                          >
                            Inspect 🔍
                          </button>
                        )}
                        <div className="muted mono" style={{ fontSize: 10 }}>{formatTime(r.createdAt)}</div>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </>
  );
}
